import { z } from 'zod';
import {
  assignInput,
  changesSchema,
  clientCreateInput,
  commentCreateInput,
  dossierCreateInput,
  transitionInput,
  uuidSchema,
} from './schemas';
import type { DossierStatus } from './states/dossier';
import {
  lineCreateInput,
  type AutoRule,
  type CatalogUnit,
  type DiscountKind,
  type PriceVersion,
  type SectionKind,
} from './catalog';
import type { AssignmentRole } from './schemas';
import type { DossierFileRecord } from './files';
import type { SaleChannel } from './legal';
import { interventionCreateInput, interventionTransitionInput,
  interventionAppointmentInput, type InterventionRecord } from './planning';
import type { CommissionRecord } from './commissions';
import type { StockIndicator } from './stock';
import { savCreateInput, savTransitionInput, type SavRecord } from './sav';
import {
  paymentCreateInput,
  paymentTransitionInput,
  type PaymentMethodRecord,
  type PaymentRecord,
} from './payments';
import { portalCreateInput, type NotificationRecord, type PortalLinkRecord } from './portal';
import type { AnnouncementRecord, ResourceRecord } from './announcements';
import { prospectContactInput, prospectLinkClientInput, type ProspectRecord } from './prospects';
import {
  signInput,
  voidSignatureInput,
  type LegalTextRecord,
  type ReserveEntry,
  type SignatureSummary,
} from './contract';

/* ------------------------------------------------------------------ records */

export interface BaseRecord {
  id: string;
  version: number;
  updatedAt: string;
}

export interface TerritoryRecord extends BaseRecord {
  code: string;
  name: string;
  timezone: string;
  currency: string;
  isActive: boolean;
  sortOrder: number;
  settings: Record<string, unknown>;
}

export interface DossierTypeRecord extends BaseRecord {
  code: string;
  label: string;
  description: string | null;
  isActive: boolean;
  sortOrder: number;
  /** { color, icon, activity: 'vente'|'pose'|'entretien'|'sav', requiresInstallConditions } */
  settings: Record<string, unknown>;
}

export interface ReasonRecord extends BaseRecord {
  category: string;
  code: string;
  label: string;
  requiresComment: boolean;
  isActive: boolean;
  sortOrder: number;
}

export interface ClientRecord extends BaseRecord {
  territoryId: string;
  kind: 'individual' | 'company';
  civility: 'M' | 'MME' | 'AUTRE' | null;
  firstName: string | null;
  lastName: string | null;
  companyName: string | null;
  siret: string | null;
  email: string | null;
  phone: string | null;
  phoneAlt: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  postalCode: string | null;
  city: string | null;
  notes: string | null;
  marketingConsent: boolean;
  contactName: string | null;
  extraPhones: { label: string | null; number: string }[];
  customFields: Record<string, string | number | boolean | null>;
  ownerUserId: string | null;
  /** client d'un partenaire (sous-traitance de pose) ; null = client de l'entreprise */
  partnerId: string | null;
  createdBy: string | null;
  createdAt: string;
}

export interface DossierAssignment {
  userId: string;
  userName: string;
  role: AssignmentRole;
  assignedAt: string;
}

export interface DossierRecord extends BaseRecord {
  reference: string | null;
  territoryId: string;
  clientId: string;
  dossierTypeId: string;
  /**
   * chantier pour un partenaire (sous-traitance de pose) : pas de vente (prix, signature, paiement,
   * commission de vente, facturation, avis) ; envoyé directement en planification
   */
  partnerId: string | null;
  title: string | null;
  description: string | null;
  status: DossierStatus;
  statusChangedAt: string;
  statusReasonId: string | null;
  siteAddressLine1: string | null;
  siteAddressLine2: string | null;
  sitePostalCode: string | null;
  siteCity: string | null;
  geoLat: number | null;
  geoLng: number | null;
  geoAccuracyM: number | null;
  geoCapturedAt: string | null;
  installWallTypeId: string | null;
  installAtHeight: boolean | null;
  installAccess: 'none' | 'ladder' | 'nacelle' | null;
  installHeightM: number | null;
  /** réserves / décharges (§4.11) — texte repris dans le contrat */
  reserves: ReserveEntry[];
  saleChannel: SaleChannel;
  proEmployeeCount: number | null;
  proOutsideMainActivity: boolean | null;
  earlyStartRequested: boolean;
  /** signature valide du client : le contrat est figé tant qu'elle n'est pas annulée */
  signature: SignatureSummary | null;
  submittedAt: string | null;
  /** dernier contrôle : identité du contrôleur visible du commercial (CDC §2) */
  lastControl: {
    outcome: 'returned' | 'validated';
    byUserId: string;
    byName: string;
    at: string;
    reasonId: string | null;
    comment: string | null;
  } | null;
  createdBy: string | null;
  createdAt: string;
  assignments: DossierAssignment[];
}

export interface CommentRecord extends BaseRecord {
  dossierId: string;
  visibility: 'public' | 'private';
  body: string;
  authorId: string;
  authorName: string;
  createdAt: string;
}

export interface CatalogSectionRecord extends BaseRecord {
  code: string;
  label: string;
  kind: SectionKind;
  sortOrder: number;
  isActive: boolean;
}

export interface CatalogFamilyRecord extends BaseRecord {
  sectionId: string;
  label: string;
  sortOrder: number;
  isActive: boolean;
}

export interface CatalogBrandRecord extends BaseRecord {
  label: string;
  sortOrder: number;
  isActive: boolean;
}

export interface CatalogItemRecord extends BaseRecord {
  sectionId: string;
  familyId: string | null;
  brandId: string | null;
  label: string;
  model: string | null;
  description: string | null;
  unit: CatalogUnit;
  powerMinBtu: number | null;
  powerMaxBtu: number | null;
  dossierTypeIds: string[];
  territoryIds: string[];
  stockMode: 'stocked' | 'unlimited';
  optionSteps: string[];
  /** classe énergétique et garantie de la variante ; image (adresse versionnée) */
  energyClass: string | null;
  warranty: string | null;
  /** ajouté automatiquement selon les conditions d'intervention (nacelle…) */
  autoRule: AutoRule | null;
  /** articles dont l'un doit figurer dans le même dossier (options liées) */
  requiresItemIds: string[];
  /** mis en avant dans un encadré du devis / bon de commande */
  quoteHighlight: boolean;
  imageUrl: string | null;
  sortOrder: number;
  isActive: boolean;
  /** versions en vigueur et à venir (par territoire) ; commissions masquées selon les droits */
  prices: PriceVersion[];
  /** §13 : disponibilité par territoire (références stockées ; jamais les quantités pour un commercial) */
  stock?: Record<string, StockIndicator>;
}

/** Ligne produit / prestation d'un dossier : libellé, prix et étapes « snapshotés ». */
export interface DossierLineRecord extends BaseRecord {
  dossierId: string;
  itemId: string;
  sortOrder: number;
  quantity: number;
  label: string;
  model: string | null;
  unit: CatalogUnit;
  optionSteps: string[];
  /** mise en avant sur le devis / bon de commande (copiée de l'article à l'ajout) */
  quoteHighlight: boolean;
  /** prix unitaire HT ; null = masqué (technicien sans droit de voir les montants) */
  unitPriceHtCents: number | null;
  vatRateBp: number;
  /** prix unitaire TTC exact (null = calculé depuis le HT) */
  unitPriceTtcCents: number | null;
  /** ancien prix TTC exact barré (null = calculé depuis le HT) */
  compareAtPriceTtcCents: number | null;
  /** remise TTC exacte (null = calculée depuis le HT) */
  discountTtcCents: number | null;
  /** ancien prix unitaire HT affiché barré, propre à ce dossier (administrateur) ; null = aucun */
  compareAtPriceHtCents: number | null;
  /** prix modifié par l'administrateur pour ce dossier (différent du catalogue) */
  priceOverridden: boolean;
  /** remise HT de la ligne (0 = aucune ; null = masquée) */
  discountHtCents: number | null;
  discountKind: DiscountKind | null;
  discountNote: string | null;
  /** figés à la validation du dossier (null avant, ou masqués selon les droits) */
  saleCommissionCents: number | null;
  /** modèle changé par l'administrateur (rupture fournisseur…) : ancien modèle, auteur, date, motif ; null = jamais */
  substitution: { at: string; byName: string; fromLabel: string; reason: string } | null;
  installRemunerationCents: number | null;
  frozenAt: string | null;
  notes: string | null;
  createdAt: string;
}

export interface EntityRecordMap {
  territory: TerritoryRecord;
  dossier_type: DossierTypeRecord;
  reason: ReasonRecord;
  client: ClientRecord;
  dossier: DossierRecord;
  comment: CommentRecord;
  catalog_section: CatalogSectionRecord;
  catalog_family: CatalogFamilyRecord;
  catalog_brand: CatalogBrandRecord;
  catalog_item: CatalogItemRecord;
  dossier_line: DossierLineRecord;
  dossier_file: DossierFileRecord;
  legal_text: LegalTextRecord;
  portal_link: PortalLinkRecord;
  notification: NotificationRecord;
  intervention: InterventionRecord;
  payment_method: PaymentMethodRecord;
  payment: PaymentRecord;
  commission: CommissionRecord;
  sav: SavRecord;
  announcement: AnnouncementRecord;
  resource: ResourceRecord;
  prospect: ProspectRecord;
}
export type SyncEntity = keyof EntityRecordMap;
export const SYNC_ENTITIES: SyncEntity[] = [
  'territory',
  'dossier_type',
  'reason',
  'legal_text',
  'catalog_section',
  'catalog_family',
  'catalog_brand',
  'catalog_item',
  'client',
  'dossier',
  'dossier_line',
  'comment',
  'dossier_file',
  'portal_link',
  'notification',
  'intervention',
  'payment_method',
  'payment',
  'commission',
  'sav',
  'announcement',
  'resource',
  'prospect',
];

/* ---------------------------------------------------------------- mutations */

const base = { mutationId: uuidSchema, entityId: uuidSchema, clientTs: z.string().optional() };

export const mutationSchema = z.discriminatedUnion('type', [
  z.object({ ...base, type: z.literal('client.create'), payload: clientCreateInput.omit({ id: true }) }),
  z.object({
    ...base,
    type: z.literal('client.update'),
    baseVersion: z.number().int(),
    changes: changesSchema,
  }),
  z.object({ ...base, type: z.literal('client.delete'), baseVersion: z.number().int() }),
  z.object({ ...base, type: z.literal('dossier.create'), payload: dossierCreateInput.omit({ id: true }) }),
  z.object({
    ...base,
    type: z.literal('dossier.update'),
    baseVersion: z.number().int(),
    changes: changesSchema,
  }),
  z.object({ ...base, type: z.literal('dossier.transition'), payload: transitionInput }),
  z.object({ ...base, type: z.literal('dossier.assign'), payload: assignInput }),
  z.object({ ...base, type: z.literal('dossier.unassign'), payload: assignInput }),
  z.object({ ...base, type: z.literal('dossier.delete'), baseVersion: z.number().int() }),
  z.object({ ...base, type: z.literal('dossier.sign'), payload: signInput }),
  z.object({ ...base, type: z.literal('dossier.void_signature'), payload: voidSignatureInput }),
  z.object({ ...base, type: z.literal('comment.create'), payload: commentCreateInput.omit({ id: true }) }),
  z.object({ ...base, type: z.literal('comment.delete') }),
  z.object({ ...base, type: z.literal('line.create'), payload: lineCreateInput.omit({ id: true }) }),
  z.object({
    ...base,
    type: z.literal('line.update'),
    baseVersion: z.number().int(),
    changes: changesSchema,
  }),
  z.object({ ...base, type: z.literal('line.delete') }),
  // fileCreateInput has refinements: validated again by the service
  z.object({ ...base, type: z.literal('file.create'), payload: z.record(z.string(), z.unknown()) }),
  z.object({
    ...base,
    type: z.literal('file.update'),
    baseVersion: z.number().int(),
    changes: changesSchema,
  }),
  z.object({ ...base, type: z.literal('file.delete') }),
  z.object({ ...base, type: z.literal('portal.create'), payload: portalCreateInput }),
  z.object({ ...base, type: z.literal('portal.revoke') }),
  z.object({ ...base, type: z.literal('notification.read') }),
  z.object({ ...base, type: z.literal('announcement.read') }),
  // refinements are checked again by the service
  z.object({ ...base, type: z.literal('prospect.contact'), payload: prospectContactInput }),
  z.object({ ...base, type: z.literal('prospect.link_client'), payload: prospectLinkClientInput }),
  z.object({
    ...base,
    type: z.literal('intervention.create'),
    payload: interventionCreateInput.omit({ id: true }),
  }),
  z.object({
    ...base,
    type: z.literal('intervention.update'),
    baseVersion: z.number().int(),
    changes: changesSchema,
  }),
  z.object({ ...base, type: z.literal('intervention.transition'), payload: interventionTransitionInput }),
  z.object({ ...base, type: z.literal('intervention.appointment'), payload: interventionAppointmentInput }),
  z.object({ ...base, type: z.literal('payment.create'), payload: paymentCreateInput.omit({ id: true }) }),
  z.object({ ...base, type: z.literal('payment.transition'), payload: paymentTransitionInput }),
  z.object({ ...base, type: z.literal('sav.create'), payload: savCreateInput.omit({ id: true }) }),
  z.object({ ...base, type: z.literal('sav.transition'), payload: savTransitionInput }),
]);
export type Mutation = z.input<typeof mutationSchema>;
export type MutationType = Mutation['type'];

export const MUTATION_ENTITY: Record<MutationType, SyncEntity> = {
  'client.create': 'client',
  'client.update': 'client',
  'client.delete': 'client',
  'dossier.create': 'dossier',
  'dossier.update': 'dossier',
  'dossier.transition': 'dossier',
  'dossier.assign': 'dossier',
  'dossier.unassign': 'dossier',
  'dossier.delete': 'dossier',
  'dossier.sign': 'dossier',
  'dossier.void_signature': 'dossier',
  'comment.create': 'comment',
  'comment.delete': 'comment',
  'line.create': 'dossier_line',
  'line.update': 'dossier_line',
  'line.delete': 'dossier_line',
  'file.create': 'dossier_file',
  'file.update': 'dossier_file',
  'file.delete': 'dossier_file',
  'portal.create': 'portal_link',
  'portal.revoke': 'portal_link',
  'notification.read': 'notification',
  'announcement.read': 'announcement',
  'prospect.contact': 'prospect',
  'prospect.link_client': 'prospect',
  'intervention.create': 'intervention',
  'intervention.update': 'intervention',
  'intervention.transition': 'intervention',
  'intervention.appointment': 'intervention',
  'payment.create': 'payment',
  'payment.transition': 'payment',
  'sav.create': 'sav',
  'sav.transition': 'sav',
};

export const pushRequestSchema = z.object({
  deviceId: z.string().min(8).max(100),
  mutations: z.array(mutationSchema).min(1).max(200),
});

export type MutationStatus = 'applied' | 'partially_applied' | 'rejected';

export interface FieldConflict {
  field: string;
  base: unknown;
  mine: unknown;
  theirs: unknown;
}

export interface MutationResult {
  mutationId: string;
  status: MutationStatus;
  /** true when the mutation had already been processed (idempotent replay) */
  duplicate?: boolean;
  conflicts?: FieldConflict[];
  error?: { code: string; message: string; details?: unknown };
  /** transient server failure: the mutation was NOT recorded, the device must retry it later */
  retryable?: boolean;
}

export interface PushResponse {
  results: MutationResult[];
}

export interface SyncChange {
  entity: SyncEntity;
  id: string;
  op: 'upsert' | 'remove';
  record?: EntityRecordMap[SyncEntity];
}

export interface PullResponse {
  changes: SyncChange[];
  cursor: string;
  hasMore: boolean;
  /** server asks the client to drop its local copy and resync (scope/permissions changed) */
  reset: boolean;
}

/* ------------------------------------------------------------------ helpers */

export function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if ((a === null || a === undefined || a === '') && (b === null || b === undefined || b === '')) return true;
  if (typeof a === 'object' && typeof b === 'object' && a && b)
    return JSON.stringify(a) === JSON.stringify(b);
  return false;
}

/**
 * Three-way field merge. For each changed field:
 *  - server value still equals the client's base  → apply the client's value
 *  - server value already equals the client's value → nothing to do
 *  - otherwise → conflict, server value wins (reported back to the client)
 */
export function mergeChanges<T extends Record<string, unknown>>(
  current: T,
  changes: Record<string, { from: unknown; to: unknown }>,
  allowed: readonly string[],
): { patch: Partial<T>; conflicts: FieldConflict[]; ignored: string[] } {
  const patch: Record<string, unknown> = {};
  const conflicts: FieldConflict[] = [];
  const ignored: string[] = [];
  for (const [field, change] of Object.entries(changes)) {
    if (!allowed.includes(field)) {
      ignored.push(field);
      continue;
    }
    const theirs = current[field];
    if (valuesEqual(theirs, change.to)) continue;
    if (valuesEqual(theirs, change.from)) patch[field] = change.to;
    else conflicts.push({ field, base: change.from, mine: change.to, theirs });
  }
  return { patch: patch as Partial<T>, conflicts, ignored };
}
